// Cards are encoded as numbers 0..51: suit = floor(n / 13), rank = n % 13 + 1. Two-deck games (Double Solitaire) number
// the second deck 52..103, so every card on the table has its own id; suit and rank read the same for both copies.
export type Card = number
export const SUITS = ["spades", "hearts", "diamonds", "clubs"] as const
export const SUIT_GLYPHS = ["♠", "♥", "♦", "♣"] as const
export const RANK_LABELS = ["A", "2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K"] as const
const RANK_NAMES = ["ace", "2", "3", "4", "5", "6", "7", "8", "9", "10", "jack", "queen", "king"] as const

export const suitOf = (c: Card): number => Math.floor((c % 52) / 13)
export const rankOf = (c: Card): number => (c % 13) + 1
export const isRed = (c: Card): boolean => suitOf(c) === 1 || suitOf(c) === 2
export const cardName = (c: Card): string => `${RANK_NAMES[rankOf(c) - 1]} of ${SUITS[suitOf(c)]}`
export const rankLabel = (c: Card): string => RANK_LABELS[rankOf(c) - 1]!
export const suitGlyph = (c: Card): string => SUIT_GLYPHS[suitOf(c)]!

export const fullDeck = (): Card[] => Array.from({ length: 52 }, (_, i) => i)

// True when `upper` can sit on `lower` in a descending, alternating-color tableau build.
export function buildsOn(upper: Card, lower: Card): boolean {
  return rankOf(lower) === rankOf(upper) + 1 && isRed(lower) !== isRed(upper)
}

// Picture faces: Adrian Kennard's CC0 deck (public/cards/v1, see script/data/cards/PROVENANCE.md).
const FILE_RANKS = "A23456789TJQK"
const FILE_SUITS = "SHDC" // same order as SUITS
export const faceSrc = (c: Card): string => `/cards/v1/${FILE_RANKS[rankOf(c) - 1]}${FILE_SUITS[suitOf(c)]}.svg`

// Each face used to download the first time its card turned over, so a drawn card showed blank white for a moment
// (owner feedback 2026-10-08). The first face drawn fetches and decodes all 52; holding the Image objects keeps them
// decoded, so later cards appear on the same frame.
let preloaded: HTMLImageElement[] | null = null
export function preloadFaces(): void {
  if (preloaded || typeof Image === "undefined" || document.documentElement.dataset.faces === "simple") return
  preloaded = fullDeck().map((c) => {
    const img = new Image()
    img.src = faceSrc(c)
    img.decode?.().catch(() => { /* a failed face falls back to the white card, as before */ })
    return img
  })
}

/** Inner HTML of a face-up card: the picture face, plus the large-letter face used when Settings → Simple.
 *  CSS shows one or the other (html[data-faces]); both are aria-hidden, as the button carries the name. */
export function faceHtml(c: Card, pips: "corner" | "spider" = "corner"): string {
  preloadFaces()
  const simple = pips === "spider"
    ? `<span class="rank">${rankLabel(c)}</span><span class="pip">${suitGlyph(c)}</span>`
    : `<span class="rank">${rankLabel(c)}</span><span class="suit">${suitGlyph(c)}</span><span class="pip">${suitGlyph(c)}</span>`
  return `<img class="face-img" src="${faceSrc(c)}" alt="" draggable="false" aria-hidden="true"><span class="simple" aria-hidden="true">${simple}</span>`
}

/** A second click on the same card within 450 ms. Each click re-renders the board, so the browser never sees two
 *  clicks on one element and its own dblclick never fires (bug hunt 2026-10-08). Keyboard clicks (detail 0) don't count. */
export class DoubleTap {
  private last: { key: string; at: number } | null = null
  hit(e: Event, key: string): boolean {
    if ((e as MouseEvent).detail === 0) { this.last = null; return false }
    const now = performance.now()
    const yes = this.last?.key === key && now - this.last.at < 450
    this.last = yes ? null : { key, at: now }
    return yes
  }
}

/** After a re-render, focus what had focus before; when it is gone (an empty pile filled, a card moved away), the
 *  last item of the same pile, else the first item on the board, so keyboard play never falls out to the page
 *  (bug hunt 2026-10-08: arrows and Ctrl+Z stopped after a move to an empty column). */
export function refocus(root: HTMLElement, key: string | undefined): void {
  if (!key) return
  // A disabled button (Spider's empty stock) can't take focus, so it never counts as a target.
  const ok = (e: HTMLElement | null): e is HTMLElement => !!e && !(e as HTMLButtonElement).disabled
  const find = (k: string): HTMLElement | null => root.querySelector<HTMLElement>(`[data-loc="${CSS.escape(k)}"], [data-focus-key="${CSS.escape(k)}"]`)
  const pile = key.split(":").slice(0, 2).join(":")
  const same = [...root.querySelectorAll<HTMLElement>("[data-loc]")].filter((e) => ok(e) && (e.dataset.loc === pile || e.dataset.loc!.startsWith(`${pile}:`)))
  const first = [...root.querySelectorAll<HTMLElement>("[data-nav]")].find(ok)
  const own = find(key)
  ;(ok(own) ? own : same[same.length - 1] ?? first)?.focus({ preventScroll: true })
}
